import { useEffect, useRef, useMemo } from 'react';
import { request } from '@core/api';
import { clearQueue, enqueue, processQueue } from '@core/api/queue';
import { errorBox } from '@core/components/AlertBox';
import { equalLengthAndValues } from '@core/logic';
import type { TrenchPrelabel } from '@core/types';
import { scanSelectors } from '@scans/redux';
import { viewer3dSelectors } from '@viewer3D/redux';
import { useSelector, shallowEqual } from 'react-redux';
import { proxy, useSnapshot } from 'valtio';

// PRELABELED TRENCHES
export const prelabeledTrenchesState = proxy<{
  show: boolean;
  trenches: TrenchPrelabel[];
}>({
  show: false,
  trenches: [],
});

export const togglePrelabeledTrenchVisibility = (): void => {
  prelabeledTrenchesState.show = !prelabeledTrenchesState.show;
};

export const hidePrelabeledTrenches = (): void => {
  prelabeledTrenchesState.show = false;
};

export const setPrelabels = (trenches: TrenchPrelabel[]): void => {
  prelabeledTrenchesState.trenches = trenches;
};

export const usePrelabeledTrenchesState = () => useSnapshot(prelabeledTrenchesState);

export const useStableArray = <T>(array: T[]): T[] => {
  const ref = useRef<T[]>(array);
  if (!equalLengthAndValues(array, ref.current)) {
    ref.current = array;
  }
  return ref.current;
};

export const useFetchPrelabeledTrenches = (rawScanIds: string[]): void => {
  const scanIds = useStableArray(rawScanIds);
  const selectScans = useMemo(() => scanSelectors.selectByIds(scanIds), [scanIds]);
  const scans = useSelector(selectScans, shallowEqual);
  const projectId = useSelector(viewer3dSelectors.selectedProjectId);

  const processingResultIds = useStableArray(
    scans.map((scan) => scan.selectedProcessingResultId).filter(Boolean) as string[],
  );

  const fetchedResultIdsRef = useRef<string[]>([]);
  const scansRef = useRef(scans);
  scansRef.current = scans;

  useEffect(() => {
    const fetchedResultIds = fetchedResultIdsRef.current;

    const isResultIdListUpdated =
      !processingResultIds.length ||
      (processingResultIds.length && !equalLengthAndValues(processingResultIds, fetchedResultIds));

    if (isResultIdListUpdated) {
      clearQueue('prelabels-queue');
    }

    if (!processingResultIds.length) {
      if (fetchedResultIds.length > 0) {
        setPrelabels([]);
      }
      fetchedResultIdsRef.current = [];
      return;
    }

    if (equalLengthAndValues(processingResultIds, fetchedResultIds)) {
      return;
    }

    fetchedResultIdsRef.current = processingResultIds;
    const requests: Promise<TrenchPrelabel>[] = [];
    const currentScans = scansRef.current;

    currentScans.forEach((scan) => {
      if (!scan.selectedProcessingResultId) return;
      enqueue('prelabels-queue', () => {
        const queuedRequest = request<TrenchPrelabel>('GET', {
          path: [
            'projects',
            projectId!,
            'processingResults',
            scan.selectedProcessingResultId!,
            'prelabels',
            'trench',
          ],
        });
        requests.push(queuedRequest);
      });
    });

    let isCurrent = true;
    const fetchPrelabels = async () => {
      try {
        processQueue('prelabels-queue');
        const prelabels = await Promise.all(requests);
        if (!isCurrent) return;

        if (!prelabels.length) {
          errorBox('No prelabeled trenches found for visible scans');
        } else {
          setPrelabels(prelabels.flat());
        }
      } catch (_error) {
        if (!isCurrent) return;
        errorBox('Error fetching prelabeled trenches');
      }
    };

    fetchPrelabels();

    return () => {
      isCurrent = false;
      clearQueue('prelabels-queue');
    };
  }, [processingResultIds, projectId]);
};
